import { getApiOrigin } from '@/config/env';
import type { SurveyLanguage } from '@shared/types/surveyLanguage';

export type RequestOtpResult =
  | { ok: true; customerUuid: string }
  | { ok: false; error: string; status: number; code?: string };

export async function requestOtp(params: {
  mobileLocalDigits: string;
  ageBand: string;
  language: SurveyLanguage;
  staffCode: string;
  turnstileToken?: string | null;
}): Promise<RequestOtpResult> {
  const res = await fetch(`${getApiOrigin()}/api/v1/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mobileLocalDigits: params.mobileLocalDigits,
      ageBand: params.ageBand,
      language: params.language,
      staffCode: params.staffCode,
      ...(params.turnstileToken ? { turnstileToken: params.turnstileToken } : {}),
    }),
  });

  const data = (await res.json()) as {
    ok?: boolean;
    customerUuid?: string;
    error?: string;
    code?: string;
  };

  if (res.ok && data.ok && typeof data.customerUuid === 'string') {
    return { ok: true, customerUuid: data.customerUuid };
  }

  return {
    ok: false,
    error: typeof data.error === 'string' ? data.error : 'Unable to send OTP. Please try again.',
    status: res.status,
    ...(typeof data.code === 'string' ? { code: data.code } : {}),
  };
}

export type ExistingCustomerProfile = {
  name: string;
  email: string;
  race: string;
  state: string;
  area: string;
};

export type VerifyOtpResult =
  | {
      ok: true;
      submissionIndex: number;
      questionnaireSet: 'A' | 'B';
      existingProfile: ExistingCustomerProfile | null;
    }
  | { ok: false; error: string; code?: string };

export async function verifyOtp(params: {
  customerUuid: string;
  otp: string;
}): Promise<VerifyOtpResult> {
  const res = await fetch(`${getApiOrigin()}/api/v1/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerUuid: params.customerUuid,
      otp: params.otp,
    }),
  });

  const data = (await res.json()) as {
    ok?: boolean;
    error?: string;
    code?: string;
    submissionIndex?: number;
    questionnaireSet?: 'A' | 'B';
    existingProfile?: {
      name?: string;
      email?: string;
      race?: string;
      state?: string;
      area?: string;
    } | null;
  };

  if (
    res.ok &&
    data.ok &&
    typeof data.submissionIndex === 'number' &&
    (data.questionnaireSet === 'A' || data.questionnaireSet === 'B')
  ) {
    let existingProfile: ExistingCustomerProfile | null = null;
    const p = data.existingProfile;
    if (
      p &&
      typeof p.name === 'string' &&
      typeof p.email === 'string' &&
      typeof p.race === 'string' &&
      typeof p.state === 'string' &&
      typeof p.area === 'string' &&
      p.name.trim() &&
      p.email.trim() &&
      p.race.trim() &&
      p.state.trim() &&
      p.area.trim()
    ) {
      existingProfile = {
        name: p.name.trim(),
        email: p.email.trim(),
        race: p.race.trim(),
        state: p.state.trim(),
        area: p.area.trim(),
      };
    }

    return {
      ok: true,
      submissionIndex: data.submissionIndex,
      questionnaireSet: data.questionnaireSet,
      existingProfile,
    };
  }

  return {
    ok: false,
    error: typeof data.error === 'string' ? data.error : 'Invalid OTP.',
    ...(typeof data.code === 'string' ? { code: data.code } : {}),
  };
}
